import { SHOP_LOGO_SIZES } from "./shopLogoTokens";
import { formatShopLocation, formatShopRating, getShopBadges, getShopBusinessLinks, buildShopServicesLabel } from "./shopDisplayHelpers";

function escapeHtml(value) {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function getLinkIconSvg(type) {
  if (type === "map") {
    return `<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z"></path><circle cx="12" cy="10" r="2.5"></circle></svg>`;
  }
  if (type === "website") {
    return `<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"></circle><path d="M3 12h18M12 3c2.5 2.5 3.5 5.5 3.5 9S14.5 18.5 12 21M12 3C9.5 5.5 8.5 8.5 8.5 12S9.5 18.5 12 21"></path></svg>`;
  }
  if (type === "facebook") {
    return `<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" fill="currentColor"><path d="M13.5 21v-8h2.7l.4-3h-3.1V8.1c0-.9.2-1.6 1.6-1.6h1.7V3.8c-.3 0-1.3-.1-2.5-.1-2.5 0-4.2 1.5-4.2 4.3V10H8v3h2.1v8h3.4z"></path></svg>`;
  }
  if (type === "google") {
    return `<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" fill="currentColor"><path d="M12.24 10.4v3.32h4.6c-.2 1.18-1.4 3.46-4.6 3.46-2.77 0-5.03-2.29-5.03-5.11s2.26-5.11 5.03-5.11c1.58 0 2.63.67 3.24 1.25l2.2-2.12C16.9 4.76 14.77 3.8 12.24 3.8 7.9 3.8 4.4 7.3 4.4 11.64s3.5 7.84 7.84 7.84c4.53 0 7.53-3.18 7.53-7.66 0-.51-.06-.9-.13-1.29l-7.4-.03z"></path></svg>`;
  }
  // Default: YouTube play glyph (type === "youtube").
  return `<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" fill="currentColor"><path d="M21.6 7.2a3.1 3.1 0 0 0-2.2-2.2C17.5 4.5 12 4.5 12 4.5s-5.5 0-7.4.5a3.1 3.1 0 0 0-2.2 2.2C2 9.1 2 12 2 12s0 2.9.4 4.8a3.1 3.1 0 0 0 2.2 2.2c1.9.5 7.4.5 7.4.5s5.5 0 7.4-.5a3.1 3.1 0 0 0 2.2-2.2c.4-1.9.4-4.8.4-4.8s0-2.9-.4-4.8ZM10.2 15.3V8.7L15.8 12l-5.6 3.3Z"></path></svg>`;
}

export function buildShopMapPopupHtml({
  shop,
  mapsUrl = "",
  specializationBadge = "",
  badges = null,
  effectiveMake = "",
  trustSummary = "",
  showFavoriteButton = false,
  isFavorite = false,
}) {
  const helperBadges = getShopBadges(shop, effectiveMake);
  const displayBadges = Array.isArray(badges)
    ? badges
    : specializationBadge
      ? [
          { key: "specialization", label: specializationBadge },
          ...helperBadges.filter((badge) => badge.key !== "specialization"),
        ]
      : helperBadges;
  const businessLinks = getShopBusinessLinks(shop, mapsUrl);
  const servicesLabel = buildShopServicesLabel(shop);
  const rating = formatShopRating(shop);
  return `
    <div style="position:relative;min-width:min(90vw,500px);max-width:640px;border:1px solid #e5e7eb;border-radius:14px;padding:14px 52px 14px 14px;background:#fff;min-height:152px;">
      <div style="display:flex;align-items:flex-start;gap:12px;">
        <div style="flex:1;min-width:0;">
          <div style="display:flex;align-items:flex-start;gap:10px;">
            ${shop.shop_logo_url ? `<img src="${escapeHtml(shop.shop_logo_url)}" alt="Shop logo" style="width:${SHOP_LOGO_SIZES.mapPopup}px;height:${SHOP_LOGO_SIZES.mapPopup}px;border-radius:12px;object-fit:contain;border:1px solid #e5e7eb;background:#fff;flex:0 0 auto;" />` : ""}
            <div style="min-width:0;">
              <div style="font-weight:700;font-size:20px;line-height:1.15;color:#111827;word-break:break-word;">${escapeHtml(shop.shop_name || "Shop")}</div>
              <div style="font-size:14px;color:#4b5563;margin-top:6px;">${escapeHtml(formatShopLocation(shop))}</div>
              ${rating ? (shop.business_google_profile_url
                ? `<a href="${escapeHtml(shop.business_google_profile_url)}" target="_blank" rel="noopener noreferrer" title="View this shop's Google reviews" style="display:inline-flex;align-items:center;gap:4px;margin-top:6px;font-size:13px;color:#374151;text-decoration:none;"><svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="#f59e0b"><path d="M12 2l2.9 6.3 6.9.7-5.1 4.6 1.4 6.8L12 17.8 5.9 20.4l1.4-6.8L2.2 9l6.9-.7z"></path></svg><span style="font-weight:600;">${escapeHtml(rating.rating)}</span><span style="color:#6b7280;">(${rating.count}) on Google</span></a>`
                : `<span style="display:inline-flex;align-items:center;gap:4px;margin-top:6px;font-size:13px;color:#374151;"><svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="#f59e0b"><path d="M12 2l2.9 6.3 6.9.7-5.1 4.6 1.4 6.8L12 17.8 5.9 20.4l1.4-6.8L2.2 9l6.9-.7z"></path></svg><span style="font-weight:600;">${escapeHtml(rating.rating)}</span><span style="color:#6b7280;">(${rating.count}) on Google</span></span>`) : ""}
            </div>
          </div>
          ${businessLinks.length ? `<div style="display:flex;gap:8px;align-items:center;margin-top:12px;">${businessLinks.map((link) => `<a href="${escapeHtml(link.href)}" target="_blank" rel="noopener noreferrer" aria-label="${escapeHtml(link.label)}" title="${escapeHtml(link.title)}" style="width:30px;height:30px;border:1px solid #d1d5db;border-radius:999px;display:inline-flex;align-items:center;justify-content:center;text-decoration:none;color:#374151;">${getLinkIconSvg(link.type)}</a>`).join("")}</div>` : ""}
          ${displayBadges.length ? `<div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:12px;">${displayBadges.map((badge) => `<span style="border:1px solid #d1d5db;border-radius:999px;padding:2px 10px;font-size:11px;line-height:1.2;color:#374151;">${escapeHtml(badge.label)}</span>`).join("")}</div>` : ""}
          ${servicesLabel ? `<div style="margin-top:10px;font-size:12px;line-height:1.3;color:#4b5563;">${escapeHtml(servicesLabel)}</div>` : ""}
          ${trustSummary ? `<div style="margin-top:10px;font-size:12px;line-height:1.25;color:#374151;">${escapeHtml(trustSummary)}</div>` : ""}
        </div>
      </div>
      ${
        showFavoriteButton
          ? `<button type="button" data-favorite-toggle="true" data-shop-id="${escapeHtml(shop.id || "")}" title="${isFavorite ? "Unfavorite shop" : "Favorite shop"}" aria-label="${isFavorite ? "Unfavorite shop" : "Favorite shop"}" style="position:absolute;right:12px;bottom:12px;width:34px;height:34px;border:1px solid #d1d5db;border-radius:999px;display:inline-flex;align-items:center;justify-content:center;background:#fff;color:${isFavorite ? "#dc2626" : "#374151"};cursor:pointer;box-shadow:0 1px 2px rgba(0,0,0,0.08);"><i class="${isFavorite ? "fi fi-sr-heart" : "fi fi-rs-heart fi-rr-heart"}" style="font-size:16px;line-height:1;font-style:normal;"></i></button>`
          : ""
      }
    </div>
  `;
}

export const SHOP_MAP_PIN_LABEL_SELECTOR = "[data-shop-map-pin-label='true']";
export const SHOP_MAP_PIN_MARKER_SELECTOR = "[data-shop-map-pin-marker='true']";

export function createShopMapPinLabelElement(label = "") {
  if (!label) return null;

  const labelEl = document.createElement("div");
  labelEl.textContent = label;
  labelEl.dataset.shopMapPinLabel = "true";
  labelEl.style.display = "block";
  labelEl.style.marginBottom = "2px";
  labelEl.style.background = "rgba(255,255,255,0.98)";
  labelEl.style.border = "1px solid #9ca3af";
  labelEl.style.borderRadius = "999px";
  labelEl.style.padding = "3px 9px";
  labelEl.style.fontSize = "11px";
  labelEl.style.fontWeight = "600";
  labelEl.style.color = "#111827";
  labelEl.style.boxShadow = "0 2px 4px rgba(0,0,0,0.18)";
  labelEl.style.whiteSpace = "nowrap";
  labelEl.style.maxWidth = "190px";
  labelEl.style.overflow = "hidden";
  labelEl.style.textOverflow = "ellipsis";
  labelEl.style.cursor = "pointer";
  return labelEl;
}

// Pin colors per shop type. Fixed shops pin to an exact location (cyan); mobile mechanics
// show a general service-area pin (violet). The legend (`MapLegend`) reuses these.
export const SHOP_PIN_COLORS = {
  shop: "#40b8d8",
  mobile: "#7c3aed",
};

// The inner glyph differs too: a solid dot for a precise shop location, a hollow ring for a
// mobile mechanic's general area.
const PIN_INNER = {
  shop: '<circle cx="12" cy="12.5" r="3.2" fill="#ffffff"></circle>',
  mobile: '<circle cx="12" cy="12.5" r="3.4" fill="none" stroke="#ffffff" stroke-width="1.8"></circle>',
};

export function createShopMapPinElement(options = {}) {
  const displayLabel = options?.label || "";
  const variant = options?.variant === "mobile" ? "mobile" : "shop";
  const fill = SHOP_PIN_COLORS[variant];
  const inner = PIN_INNER[variant];
  const labelBuilder = options?.createLabelElement || createShopMapPinLabelElement;
  const markerEl = document.createElement("div");
  markerEl.dataset.shopMapPinMarker = "true";
  markerEl.dataset.shopMapPinVariant = variant;
  markerEl.style.display = "inline-flex";
  markerEl.style.flexDirection = "column";
  markerEl.style.alignItems = "center";
  markerEl.style.justifyContent = "flex-end";
  markerEl.style.cursor = "pointer";

  const labelEl = labelBuilder(displayLabel);
  if (labelEl) {
    markerEl.appendChild(labelEl);
  }

  const pinEl = document.createElement("div");
  pinEl.innerHTML = `<svg viewBox="0 0 24 34" width="24" height="34" aria-hidden="true"><path d="M12 33S2 23.9 2 12.5C2 6.4 6.5 2 12 2s10 4.4 10 10.5C22 23.9 12 33 12 33Z" fill="${fill}" stroke="#ffffff" stroke-width="1.5"></path>${inner}</svg>`;
  pinEl.style.width = "24px";
  pinEl.style.height = "34px";
  pinEl.style.filter = "drop-shadow(0 2px 2px rgba(0,0,0,0.35))";
  markerEl.appendChild(pinEl);

  return markerEl;
}

// Single source of truth for shop map markers, shared by every map view
// (shop explorer, car request, etc.). The `bottom` anchor pins the tip of the
// pin to the exact shop coordinate so it stays put at all zoom levels - change
// marker construction here, not at the call sites.
export function createShopMapMarker(mapboxgl, options = {}) {
  return new mapboxgl.Marker({
    element: createShopMapPinElement({
      label: options?.label || "Shop",
      variant: options?.variant === "mobile" ? "mobile" : "shop",
      createLabelElement: createShopMapPinLabelElement,
    }),
    anchor: "bottom",
  });
}

export const SHOP_MAP_CLUSTER_SELECTOR = "[data-shop-map-cluster='true']";

// A cluster is a symmetric badge, so it anchors at its CENTER on the cluster centroid -
// this is intentionally different from the shop pin's `bottom` anchor and does not touch
// the exact pin placement. Size scales gently with the number of shops it represents.
export function createShopClusterElement(count = 0) {
  const n = Number(count) || 0;
  const size = n >= 50 ? 46 : n >= 10 ? 40 : 34;
  const el = document.createElement("div");
  el.dataset.shopMapCluster = "true";
  el.textContent = String(n);
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.display = "flex";
  el.style.alignItems = "center";
  el.style.justifyContent = "center";
  el.style.borderRadius = "999px";
  el.style.background = "#334155";
  el.style.color = "#ffffff";
  el.style.fontWeight = "700";
  el.style.fontSize = n >= 100 ? "12px" : "13px";
  el.style.border = "2px solid #ffffff";
  el.style.boxShadow = "0 2px 6px rgba(0,0,0,0.35)";
  el.style.cursor = "pointer";
  el.style.lineHeight = "1";
  return el;
}

export function createShopClusterMarker(mapboxgl, { count = 0, onClick } = {}) {
  const element = createShopClusterElement(count);
  if (typeof onClick === "function") {
    element.addEventListener("click", (event) => {
      event.stopPropagation();
      onClick(event);
    });
  }
  return new mapboxgl.Marker({ element, anchor: "center" });
}
